import { useEffect, useState } from 'react';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { SiteLink as Link } from './SiteLink';
import { APP_STORE_URL } from '../constants/site';
import { analytics } from '../services/analytics';

const links = [
    { name: 'Product', id: 'product' },
    { name: 'Recovery', id: 'training-recovery' },
    { name: 'How it works', id: 'how-it-works' },
    { name: 'FAQ', id: 'faq' },
];
const productPages = [
    { name: 'Nova', href: '/ai-nutrition-coach' },
    { name: 'Meal logging', href: '/meal-logging' },
];

export const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const isHome = window.location.pathname === '/';

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 16);
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false);
        };
        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [isOpen]);

    return (
        <nav className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${scrolled ? 'border-white/10 bg-background/85 backdrop-blur-xl' : 'border-transparent bg-transparent'}`} aria-label="Primary navigation">
            <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:font-bold focus:text-black">
                Skip to content
            </a>
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <Link to="/" className="flex h-full items-center rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-label="Nurvo home">
                    <img src="/assets/visuals/nurvo-logo-128.webp" alt="" width="128" height="128" className="h-16 w-auto max-w-16 object-contain" />
                </Link>

                <div className="hidden items-center gap-8 md:flex">
                    {links.map((link) => (
                        <a key={link.id} href={isHome ? `#${link.id}` : `/#${link.id}`} className="rounded text-sm font-medium text-text-secondary transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                            {link.name}
                        </a>
                    ))}
                    {productPages.map((link) => <Link key={link.href} to={link.href} className="rounded text-sm font-medium text-text-secondary transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">{link.name}</Link>)}
                    <Link to="/pricing" className="rounded text-sm font-medium text-text-secondary transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">Pricing</Link>
                    <Link to="/about" className="rounded text-sm font-medium text-text-secondary transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">About</Link>
                </div>

                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="button-primary hidden py-2.5 md:inline-flex" onClick={() => analytics.appStoreClick('header', 'Download app')}>
                    Download app <ArrowUpRight size={16} aria-hidden="true" />
                </a>

                <button
                    type="button"
                    className="rounded-lg border border-white/10 p-2.5 text-white md:hidden"
                    onClick={() => setIsOpen((value) => !value)}
                    aria-expanded={isOpen}
                    aria-controls="mobile-navigation"
                    aria-label={isOpen ? 'Close menu' : 'Open menu'}
                >
                    {isOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

                {isOpen && (
                    <div id="mobile-navigation" className="overflow-hidden border-t border-white/10 bg-background/95 backdrop-blur-xl md:hidden">
                        <div className="space-y-1 px-4 py-5">
                            {links.map((link) => (
                                <a key={link.id} href={isHome ? `#${link.id}` : `/#${link.id}`} onClick={() => setIsOpen(false)} className="block rounded-xl px-4 py-3 text-base font-medium text-text-secondary hover:bg-white/5 hover:text-white">{link.name}</a>
                            ))}
                            {productPages.map((link) => <Link key={link.href} to={link.href} onClick={() => setIsOpen(false)} className="block rounded-xl px-4 py-3 text-base font-medium text-text-secondary hover:bg-white/5 hover:text-white">{link.name}</Link>)}
                            <Link to="/pricing" onClick={() => setIsOpen(false)} className="block rounded-xl px-4 py-3 text-base font-medium text-text-secondary hover:bg-white/5 hover:text-white">Pricing</Link>
                            <Link to="/about" onClick={() => setIsOpen(false)} className="block rounded-xl px-4 py-3 text-base font-medium text-text-secondary hover:bg-white/5 hover:text-white">About</Link>
                            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => { setIsOpen(false); analytics.appStoreClick('mobile_menu', 'Download on the App Store'); }} className="button-primary mt-4 flex w-full">Download on the App Store</a>
                        </div>
                    </div>
                )}
        </nav>
    );
};
